import React, { useState, useEffect, useRef } from 'react';
import { Home, ShoppingCart, Trophy, TrendingUp, Flame, Target, Award, Footprints, Zap, PlayCircle, PauseCircle } from 'lucide-react';

const API_BASE = '/padavali/api';

interface UserData {
  id: string;
  username: string;
  email: string;
  wallet: {
    ahn: number;
    urja: number;
  };
}

interface Progress {
  total_steps: number;
  total_urja_earned: number;
  current_level: number;
  daily_steps_today: number;
  daily_login_streak: number;
}

interface Challenge {
  challenge_id: string;
  challenge_type: string;
  target_value: number;
  current_progress: number;
  reward_urja: number;
  completed: boolean;
  expires_at: string;
}

interface LeaderboardEntry {
  user_id: string;
  username: string;
  total_steps: number;
  total_urja: number;
  level: number;
  rank: number;
}

type ViewMode = 'loading' | 'dashboard' | 'tracker' | 'challenges' | 'leaderboard' | 'shop';

const LEVEL_NAMES = ['Beginner Walker', 'Casual Stroller', 'Active Wanderer', 'Dedicated Hiker', 'Marathon Master', 'Padavali Legend'];
const LEVEL_THRESHOLDS = [0, 10000, 50000, 100000, 250000, 500000];

function App() {
  const [user, setUser] = useState<UserData | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('loading');

  // Challenges state
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(false);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Step tracking state
  const [isTracking, setIsTracking] = useState(false);
  const [sessionSteps, setSessionSteps] = useState(0);
  const [sensorSupported, setSensorSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<string>('unknown');

  // Active boosts
  const [activeBoosts, setActiveBoosts] = useState<any[]>([]);

  // Accelerometer refs
  const lastAcceleration = useRef<number>(0);
  const stepThreshold = useRef<number>(1.2);
  const lastStepTime = useRef<number>(0);
  const sessionStepsRef = useRef<number>(0);
  const accelerometerRef = useRef<any>(null);
  const syncIntervalRef = useRef<any>(null);

  const token = localStorage.getItem('token');

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/padavali/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }

    // Check sensor support
    if ('Accelerometer' in window) {
      setSensorSupported(true);
    } else if (window.DeviceMotionEvent) {
      setSensorSupported(true);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      if (!token) {
        setError('Please login via main Ahantravyuh app');
        setLoading(false);
        return;
      }

      // Load user data
      const userRes = await fetch(`${API_BASE}/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userData = await userRes.json();
      setUser(userData);

      // Load progress
      await loadProgress();

      // Claim daily login bonus automatically
      try {
        const loginRes = await fetch(`${API_BASE}/daily-login`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (loginRes.ok) {
          const loginData = await loginRes.json();
          console.log(loginData.message);
          // Reload user data to get updated URJA
          const userRes2 = await fetch(`${API_BASE}/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setUser(await userRes2.json());
        }
      } catch (e) {
        // Already claimed or error - ignore
      }

      setLoading(false);
      setViewMode('dashboard');
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const progressRes = await fetch(`${API_BASE}/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const progressData = await progressRes.json();
      setProgress(progressData);
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  const loadChallenges = async () => {
    setChallengesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/challenges/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setChallenges(data.challenges || []);
    } catch (err) {
      console.error('Failed to load challenges:', err);
    } finally {
      setChallengesLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const res = await fetch(`${API_BASE}/leaderboard/global`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const loadActiveBoosts = async () => {
    try {
      const res = await fetch(`${API_BASE}/shop/active-boosts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setActiveBoosts(data.active_boosts || []);
    } catch (err) {
      console.error('Failed to load boosts:', err);
    }
  };

  // Step detection algorithm
  const detectStep = (acceleration: number) => {
    const now = Date.now();
    const timeSinceLastStep = now - lastStepTime.current;

    // Debounce: minimum 200ms between steps (max 300 steps/min)
    if (timeSinceLastStep < 200) return;

    // Detect peak (acceleration spike above threshold)
    const deltaAccel = acceleration - lastAcceleration.current;
    if (deltaAccel > stepThreshold.current) {
      lastStepTime.current = now;
      sessionStepsRef.current += 1;
      setSessionSteps(sessionStepsRef.current);
      console.log('Step detected! Total session steps:', sessionStepsRef.current);
    }

    lastAcceleration.current = acceleration;
  };

  // Start accelerometer tracking
  const startTracking = async () => {
    try {
      // Try Generic Sensor API first (Chrome/Edge)
      if ('Accelerometer' in window) {
        try {
          // @ts-ignore
          const accel = new Accelerometer({ frequency: 60 });
          accel.addEventListener('reading', () => {
            // @ts-ignore
            const { x, y, z } = accel;
            const magnitude = Math.sqrt(x * x + y * y + z * z);
            detectStep(magnitude);
          });
          accel.start();
          accelerometerRef.current = accel;
          setIsTracking(true);
          setPermissionStatus('granted');
          console.log('Accelerometer started (Generic Sensor API)');
        } catch (err: any) {
          console.error('Accelerometer error:', err);
          setPermissionStatus('denied');
          alert('Accelerometer permission denied. Please enable motion sensors in browser settings.');
          return;
        }
      }
      // Fallback to DeviceMotion API (Safari/iOS)
      else if (window.DeviceMotionEvent) {
        // @ts-ignore
        if (typeof DeviceMotionEvent.requestPermission === 'function') {
          // iOS 13+ requires permission
          // @ts-ignore
          const permission = await DeviceMotionEvent.requestPermission();
          if (permission !== 'granted') {
            setPermissionStatus('denied');
            alert('Motion permission denied');
            return;
          }
        }

        const handleMotion = (event: DeviceMotionEvent) => {
          const accel = event.accelerationIncludingGravity;
          if (accel && accel.x !== null && accel.y !== null && accel.z !== null) {
            const magnitude = Math.sqrt(accel.x * accel.x + accel.y * accel.y + accel.z * accel.z);
            detectStep(magnitude);
          }
        };

        window.addEventListener('devicemotion', handleMotion);
        accelerometerRef.current = handleMotion;
        setIsTracking(true);
        setPermissionStatus('granted');
        console.log('DeviceMotion started');
      } else {
        alert('Your device does not support motion sensors');
        return;
      }

      // Auto-sync steps every 30 seconds
      syncIntervalRef.current = setInterval(() => {
        if (sessionStepsRef.current > 0) {
          syncSteps(sessionStepsRef.current);
        }
      }, 30000);

    } catch (err) {
      console.error('Failed to start tracking:', err);
      alert('Failed to start step tracking');
    }
  };

  // Stop tracking
  const stopTracking = () => {
    if (accelerometerRef.current) {
      if ('Accelerometer' in window && accelerometerRef.current.stop) {
        accelerometerRef.current.stop();
      } else if (window.DeviceMotionEvent) {
        window.removeEventListener('devicemotion', accelerometerRef.current);
      }
      accelerometerRef.current = null;
    }

    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    // Sync remaining steps before stopping
    if (sessionStepsRef.current > 0) {
      syncSteps(sessionStepsRef.current);
    }

    setIsTracking(false);
    console.log('Tracking stopped');
  };

  // Sync steps to backend
  const syncSteps = async (steps: number) => {
    if (steps < 1) return;

    try {
      const res = await fetch(`${API_BASE}/steps/record`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ steps })
      });

      const data = await res.json();
      if (data.success) {
        console.log(`Synced ${steps} steps:`, data.message);
        // Reset session steps after successful sync
        sessionStepsRef.current = 0;
        setSessionSteps(0);
        await loadProgress();
        // Reload user to get updated URJA
        const userRes = await fetch(`${API_BASE}/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUser(await userRes.json());
      } else {
        console.error('Sync failed:', data.error);
      }
    } catch (err) {
      console.error('Failed to sync steps:', err);
    }
  };

  const claimChallenge = async (challengeId: string) => {
    try {
      const res = await fetch(`${API_BASE}/challenges/claim/${challengeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await loadChallenges();
        // Reload user to get updated URJA
        const userRes = await fetch(`${API_BASE}/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUser(await userRes.json());
      } else {
        alert(data.error || 'Failed to claim challenge');
      }
    } catch (err) {
      alert('Failed to claim challenge');
    }
  };

  const buyBoost = async () => {
    if (!user || user.wallet.ahn < 5) {
      alert('Insufficient AHN balance (need 5 AHN)');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/shop/buy-boost`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success) {
        alert(data.message);
        await loadActiveBoosts();
        // Reload user to get updated AHN
        const userRes = await fetch(`${API_BASE}/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUser(await userRes.json());
      } else {
        alert(data.error || 'Failed to buy boost');
      }
    } catch (err) {
      alert('Failed to buy boost');
    }
  };

  const getChallengeTitle = (type: string) => {
    switch (type) {
      case 'morning_walker': return 'Morning Walker';
      case 'distance_tracker': return 'Distance Tracker';
      case 'consistency_master': return 'Consistency Master';
      case 'weekend_warrior': return 'Weekend Warrior';
      default: return type;
    }
  };

  const getChallengeDescription = (type: string, target: number) => {
    switch (type) {
      case 'morning_walker': return `Walk ${target} steps before 10 AM`;
      case 'distance_tracker': return `Complete ${target} steps in a day`;
      case 'consistency_master': return `Walk ${target} steps today`;
      case 'weekend_warrior': return `Walk ${target} steps on weekend`;
      default: return `Complete ${target} steps`;
    }
  };

  const getLevelProgress = () => {
    if (!progress) return 0;
    const currentLevel = progress.current_level;
    const currentThreshold = LEVEL_THRESHOLDS[currentLevel - 1];
    const nextThreshold = LEVEL_THRESHOLDS[currentLevel] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
    const progressInLevel = progress.total_steps - currentThreshold;
    const levelRange = nextThreshold - currentThreshold;
    return (progressInLevel / levelRange) * 100;
  };

  if (loading || viewMode === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl">🚶 Loading PADAVALI...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-red-800 text-xl font-bold mb-4">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Navigation Bar
  const NavBar = () => (
    <div className="bg-white shadow-md sticky top-0 z-10">
      <div className="flex justify-around items-center p-3">
        <button
          onClick={() => setViewMode('dashboard')}
          className={`flex flex-col items-center ${viewMode === 'dashboard' ? 'text-purple-600' : 'text-gray-500'}`}
        >
          <Home size={24} />
          <span className="text-xs mt-1">Home</span>
        </button>
        <button
          onClick={() => setViewMode('tracker')}
          className={`flex flex-col items-center ${viewMode === 'tracker' ? 'text-purple-600' : 'text-gray-500'}`}
        >
          <Footprints size={24} />
          <span className="text-xs mt-1">Track</span>
        </button>
        <button
          onClick={() => { setViewMode('challenges'); loadChallenges(); }}
          className={`flex flex-col items-center ${viewMode === 'challenges' ? 'text-purple-600' : 'text-gray-500'}`}
        >
          <Target size={24} />
          <span className="text-xs mt-1">Challenges</span>
        </button>
        <button
          onClick={() => { setViewMode('leaderboard'); loadLeaderboard(); }}
          className={`flex flex-col items-center ${viewMode === 'leaderboard' ? 'text-purple-600' : 'text-gray-500'}`}
        >
          <Trophy size={24} />
          <span className="text-xs mt-1">Rank</span>
        </button>
        <button
          onClick={() => { setViewMode('shop'); loadActiveBoosts(); }}
          className={`flex flex-col items-center ${viewMode === 'shop' ? 'text-purple-600' : 'text-gray-500'}`}
        >
          <ShoppingCart size={24} />
          <span className="text-xs mt-1">Shop</span>
        </button>
      </div>
    </div>
  );

  // Dashboard View
  if (viewMode === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-b-3xl shadow-lg">
            <h1 className="text-2xl font-bold mb-1">🚶 PADAVALI</h1>
            <p className="text-purple-100 text-sm">पदावली - Series of Steps</p>
            <div className="mt-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-purple-200">Welcome back,</p>
                <p className="text-xl font-semibold">{user?.username}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-purple-200">Daily Streak</p>
                <div className="flex items-center gap-1 text-xl">
                  <Flame className="text-orange-400" size={20} />
                  <span className="font-bold">{progress?.daily_login_streak || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Balances */}
          <div className="p-4 grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-4 shadow">
              <p className="text-gray-600 text-xs">AHN Balance</p>
              <p className="text-2xl font-bold text-yellow-600">{user?.wallet.ahn.toFixed(0)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow">
              <p className="text-gray-600 text-xs">URJA Balance</p>
              <p className="text-2xl font-bold text-orange-600">{user?.wallet.urja.toFixed(2)}</p>
            </div>
          </div>

          {/* Today's Progress */}
          <div className="p-4">
            <div className="bg-white rounded-xl p-5 shadow">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="text-purple-600" size={20} />
                Today's Progress
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 text-sm">Steps Today</p>
                  <p className="text-3xl font-bold text-purple-600">{progress?.daily_steps_today.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">/ 10,000 max</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Total Steps</p>
                  <p className="text-2xl font-bold text-blue-600">{progress?.total_steps.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Level Progress */}
          <div className="px-4 pb-4">
            <div className="bg-white rounded-xl p-5 shadow">
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Award className="text-purple-600" size={20} />
                Level {progress?.current_level || 1}
              </h2>
              <p className="text-sm text-gray-600 mb-2">{LEVEL_NAMES[(progress?.current_level || 1) - 1]}</p>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all"
                  style={{ width: `${getLevelProgress()}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {progress?.total_steps.toLocaleString()} / {LEVEL_THRESHOLDS[progress?.current_level || 1]?.toLocaleString()} steps
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 grid grid-cols-2 gap-3 pb-20">
            <button
              onClick={() => setViewMode('tracker')}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-xl font-bold shadow-lg"
            >
              Start Walking
            </button>
            <button
              onClick={() => { setViewMode('challenges'); loadChallenges(); }}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-bold shadow-lg"
            >
              Challenges
            </button>
          </div>

          <NavBar />
        </div>
      </div>
    );
  }

  // Step Tracker View
  if (viewMode === 'tracker') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 to-blue-100">
        <div className="max-w-md mx-auto">
          <div className="bg-white p-6 m-4 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Footprints className="text-green-600" size={28} />
              Step Tracker
            </h2>

            {!sensorSupported ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-red-800 font-semibold">Sensor Not Supported</p>
                <p className="text-sm text-red-700">Your device does not support motion sensors.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Today's Steps: <span className="font-bold text-green-600">{progress?.daily_steps_today || 0}</span> / 10,000</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${((progress?.daily_steps_today || 0) / 10000) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Real-time session steps */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 mb-4 text-center">
                  <p className="text-sm opacity-90">Session Steps</p>
                  <p className="text-6xl font-bold">{sessionSteps}</p>
                  <p className="text-sm opacity-75 mt-2">Auto-syncs every 30 seconds</p>
                </div>

                {/* Start/Stop Button */}
                <button
                  onClick={isTracking ? stopTracking : startTracking}
                  className={`w-full py-6 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-3 ${
                    isTracking
                      ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                      : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                  }`}
                >
                  {isTracking ? (
                    <>
                      <PauseCircle size={32} />
                      Stop Tracking
                    </>
                  ) : (
                    <>
                      <PlayCircle size={32} />
                      Start Walking
                    </>
                  )}
                </button>

                {isTracking && (
                  <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-3">
                    <p className="text-sm text-blue-800">
                      🚶 Tracking active! Steps are counted automatically as you walk.
                    </p>
                  </div>
                )}

                <div className="mt-4 bg-orange-50 border-l-4 border-orange-500 p-3">
                  <p className="text-sm text-orange-800">
                    <Zap className="inline text-orange-500" size={16} /> <strong>Conversion:</strong> 1000 steps = 1 URJA
                  </p>
                  {activeBoosts.length > 0 && (
                    <p className="text-sm text-orange-600 mt-1">
                      ⚡ 2x Boost Active! Double URJA earnings!
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="p-4">
            <div className="bg-white p-4 rounded-xl shadow">
              <h3 className="font-bold mb-2">Stats</h3>
              <p className="text-sm text-gray-600">Total Steps: <span className="font-bold">{progress?.total_steps.toLocaleString()}</span></p>
              <p className="text-sm text-gray-600">Total URJA Earned: <span className="font-bold text-orange-600">{progress?.total_urja_earned.toFixed(2)}</span></p>
            </div>
          </div>

          <div className="pb-20"></div>
          <NavBar />
        </div>
      </div>
    );
  }

  // Challenges View
  if (viewMode === 'challenges') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 to-yellow-100">
        <div className="max-w-md mx-auto p-4">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Target className="text-orange-600" size={28} />
            Daily Challenges
          </h2>

          {challengesLoading ? (
            <p>Loading challenges...</p>
          ) : challenges.length === 0 ? (
            <p>No active challenges</p>
          ) : (
            <div className="space-y-3">
              {challenges.map((challenge) => {
                const progress = (challenge.current_progress / challenge.target_value) * 100;
                const canClaim = challenge.current_progress >= challenge.target_value && !challenge.completed;

                return (
                  <div key={challenge.challenge_id} className="bg-white rounded-xl p-4 shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-lg">{getChallengeTitle(challenge.challenge_type)}</h3>
                        <p className="text-sm text-gray-600">{getChallengeDescription(challenge.challenge_type, challenge.target_value)}</p>
                      </div>
                      <div className="bg-orange-100 px-3 py-1 rounded-full">
                        <p className="text-xs font-bold text-orange-700">+{challenge.reward_urja} URJA</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-bold">{challenge.current_progress} / {challenge.target_value}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${challenge.completed ? 'bg-green-500' : 'bg-orange-500'}`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {challenge.completed ? (
                      <div className="bg-green-100 text-green-700 py-2 rounded-lg text-center font-semibold">
                        ✓ Completed
                      </div>
                    ) : canClaim ? (
                      <button
                        onClick={() => claimChallenge(challenge.challenge_id)}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-bold"
                      >
                        Claim Reward
                      </button>
                    ) : (
                      <div className="bg-gray-100 text-gray-600 py-2 rounded-lg text-center text-sm">
                        Keep walking to complete!
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="pb-20"></div>
          <NavBar />
        </div>
      </div>
    );
  }

  // Leaderboard View
  if (viewMode === 'leaderboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-100 to-orange-100">
        <div className="max-w-md mx-auto p-4">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Trophy className="text-yellow-600" size={28} />
            Global Leaderboard
          </h2>

          {leaderboardLoading ? (
            <p>Loading leaderboard...</p>
          ) : leaderboard.length === 0 ? (
            <p>No leaderboard data yet</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, index) => {
                const isCurrentUser = entry.user_id === user?.id;
                const medalColor = index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-orange-600' : 'text-gray-600';

                return (
                  <div
                    key={entry.user_id}
                    className={`bg-white rounded-xl p-4 shadow flex items-center gap-3 ${isCurrentUser ? 'border-2 border-purple-500' : ''}`}
                  >
                    <div className={`text-2xl font-bold ${medalColor} w-8 text-center`}>
                      {index < 3 ? '🏆' : entry.rank}
                    </div>
                    <div className="flex-1">
                      <p className={`font-bold ${isCurrentUser ? 'text-purple-600' : ''}`}>
                        {entry.username} {isCurrentUser && '(You)'}
                      </p>
                      <p className="text-sm text-gray-600">Level {entry.level}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{entry.total_steps.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">steps</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pb-20"></div>
          <NavBar />
        </div>
      </div>
    );
  }

  // Shop View
  if (viewMode === 'shop') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100">
        <div className="max-w-md mx-auto p-4">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <ShoppingCart className="text-purple-600" size={28} />
            Shop
          </h2>

          <div className="bg-white rounded-xl p-4 mb-4 shadow">
            <p className="text-center text-sm text-gray-600">Your AHN Balance</p>
            <p className="text-4xl font-bold text-center text-yellow-600">{user?.wallet.ahn.toFixed(0)}</p>
          </div>

          {/* Active Boosts */}
          {activeBoosts.length > 0 && (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 mb-4">
              <h3 className="font-bold text-orange-700 mb-2">⚡ Active Boosts</h3>
              {activeBoosts.map((boost, idx) => (
                <p key={idx} className="text-sm text-orange-600">
                  2x Steps Boost - Active until {new Date(boost.active_until).toLocaleTimeString()}
                </p>
              ))}
            </div>
          )}

          {/* Shop Items */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold">2x Steps Boost</h3>
                  <p className="text-sm text-gray-600">Double URJA earnings for 1 hour</p>
                </div>
                <Zap className="text-orange-500" size={48} />
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-600">Benefits:</p>
                <ul className="text-sm text-gray-700 ml-4 list-disc">
                  <li>Earn 2 URJA per 1000 steps</li>
                  <li>Lasts for 1 hour</li>
                  <li>Stackable with challenges</li>
                </ul>
              </div>
              <button
                onClick={buyBoost}
                disabled={!user || user.wallet.ahn < 5}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 rounded-lg font-bold disabled:opacity-50"
              >
                Buy for 5 AHN
              </button>
            </div>
          </div>

          <div className="pb-20"></div>
          <NavBar />
        </div>
      </div>
    );
  }

  return null;
}

export default App;
